import { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router";
import { LayoutDashboard, ListTree, FolderKanban, Users, LogOut, Menu, X } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { queryClient } from "@/app/App";

export default function AdminLayout() {
  const logout = useAuthStore((state) => state.logout);
  const { data: currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    // Navigate first (with no `from` state) BEFORE clearing auth,
    // so AuthGuard never races to set { from: /admin/admins } in location state.
    navigate("/auth/login", { replace: true, state: null });
    logout();
    queryClient.clear();
  };

  const navLinks = (
    <nav className="mt-2 flex-1 space-y-1 px-3">
      <Link
        to="/admin"
        onClick={() => setSidebarOpen(false)}
        className="flex items-center px-4 py-3 text-main-1 hover:bg-white hover:rounded-lg transition-colors font-medium"
      >
        <LayoutDashboard className="w-5 h-5 mr-3 shrink-0" />
        Dashboard
      </Link>
      <Link
        to="/admin/features"
        onClick={() => setSidebarOpen(false)}
        className="flex items-center px-4 py-3 text-main-1 hover:bg-white hover:rounded-lg transition-colors font-medium"
      >
        <ListTree className="w-5 h-5 mr-3 shrink-0" />
        Features
      </Link>
      <Link
        to="/admin/projects"
        onClick={() => setSidebarOpen(false)}
        className="flex items-center px-4 py-3 text-main-1 hover:bg-white hover:rounded-lg transition-colors font-medium"
      >
        <FolderKanban className="w-5 h-5 mr-3 shrink-0" />
        Projects
      </Link>
      {currentUser?.is_super_admin && (
        <Link
          to="/admin/admins"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center px-4 py-3 text-main-1 hover:bg-white hover:rounded-lg transition-colors font-medium"
        >
          <Users className="w-5 h-5 mr-3 shrink-0" />
          Admins
        </Link>
      )}
    </nav>
  );

  const logoutBtn = (
    <div className="p-3 border-t border-main-1/10">
      <button
        onClick={handleLogout}
        className="flex items-center w-full px-4 py-3 text-red-600 hover:bg-red-50 hover:rounded-lg transition-colors font-medium"
      >
        <LogOut className="w-5 h-5 mr-3 shrink-0" />
        Logout
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background-2">

      {/* ── Mobile top bar ── */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 h-14 bg-main-2 border-b border-main-1/10 shadow-sm">
        <h1 className="text-lg font-bold text-main-1">Admin Panel</h1>
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg text-main-1 hover:bg-white transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      </header>

      {/* ── Mobile backdrop ── */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Mobile drawer ── */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-main-2 border-r border-main-1/10 flex flex-col shadow-xl transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-main-1/10">
          <h1 className="text-xl font-bold text-main-1">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-lg text-main-1 hover:bg-white transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {navLinks}
        {logoutBtn}
      </aside>

      {/* ── Desktop sidebar (always visible) ── */}
      <aside className="hidden lg:flex w-64 bg-main-2 border-r border-main-1/10 flex-col shadow-sm shrink-0">
        <div className="p-6 border-b border-main-1/10">
          <h1 className="text-xl font-bold text-main-1">Admin Panel</h1>
        </div>
        {navLinks}
        {logoutBtn}
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto pt-18 lg:pt-8">
        <Outlet />
      </main>
    </div>
  );
}
